// BB-3 (docs/build_bot_platform.md §3.3): client-side response cache.
//
// bot_builder.md proposed a server-side LRU prompt→response cache; that would
// persist confidential tenant content on the server and is FORBIDDEN under the
// hybrid local-first architecture. The correction: the cache lives HERE — a
// Dexie table (`responseCache`, schemas/dexie.schema.js) that never leaves the
// browser — is content-addressed (model identity + normalized request hash),
// expires after 24h, and is consulted only for identical re-sends.
//
// A cache hit consumes no Agent Operations: no provider call happened.

import {localDb} from '../data/localDb.js';

export const RESPONSE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// Deterministic non-crypto hash (FNV-1a). Not security-sensitive: it only
// distinguishes requests from each other. Synchronous so callers can build
// keys without await, and identical in node tests and the browser.
function fnv1a(text) {
  let hash=0x811c9dc5;

  for (let index=0; index<text.length; index++) {
    hash^=text.charCodeAt(index);
    hash=Math.imul(hash,0x01000193);
  }

  return (hash>>>0).toString(36);
}

// Normalize the request so trivially-different-but-equivalent sends hit the
// same key: trim, collapse whitespace, sort top-level message fields.
function normalizeMessages(messages) {
  return (Array.isArray(messages) ? messages : [messages])
    .map((message)=> {
      if (typeof message!=='object' || message===null) return String(message || '').trim();

      return Object.keys(message)
        .sort()
        .map((key)=> `${key}:${String(message[key] ?? '').replace(/\s+/g,' ').trim()}`)
        .join('|');
    })
    .join('\n');
}

export function buildCacheKey({modelKey,messages}) {
  return `${modelKey}::${fnv1a(normalizeMessages(messages))}`;
}

export function isCacheEntryFresh(entry,now=Date.now()) {
  return Boolean(
    entry
    && entry.created
    && now - new Date(entry.created).getTime() < RESPONSE_CACHE_TTL_MS
  );
}

/**
 * Look up a cached response. Returns `{hit:true,text,fromCache:true}` on a
 * fresh hit, `{hit:false}` otherwise. Never throws — a cache failure must not
 * break the send path.
 */
export async function getCachedResponse({modelKey,messages}) {
  try {
    const key=buildCacheKey({modelKey,messages});
    const entry=await localDb.table('responseCache').get(key);

    if (!isCacheEntryFresh(entry)) return {hit: false};

    return {hit: true,text: entry.text,fromCache: true};
  } catch {
    return {hit: false};
  }
}

/**
 * Store a response. Fire-and-forget safe; never throws.
 */
export async function putCachedResponse({modelKey,messages,text}) {
  try {
    const key=buildCacheKey({modelKey,messages});

    await localDb.table('responseCache').put({
      key,
      text,
      created: new Date().toISOString()
    });
  } catch {
    // cache write failure is non-fatal by design
  }
}

/**
 * Purge expired entries (call opportunistically — e.g. on app start). Also
 * exposed for the local export path so tenants can clear cached content.
 */
export async function purgeExpiredCacheEntries(now=Date.now()) {
  try {
    const stale=await localDb.table('responseCache')
      .filter((entry)=> !isCacheEntryFresh(entry,now))
      .primaryKeys();

    await localDb.table('responseCache').bulkDelete(stale);
    return stale.length;
  } catch {
    return 0;
  }
}
